/**
 * Phase 6 — public landing page for B2B invitation recipients. The Convex
 * action sets `inviteRedirectUrl: https://repancy.site/share/<clientSlug>`
 * on Graph POST /invitations. After Microsoft redeems the invitation, the
 * recipient lands here.
 *
 * The page intentionally:
 *   - Does NOT call any Graph endpoint (no recipient token available).
 *   - Loads minimal portal context via `portalInvitations.lookupForSharePage`
 *     so we can show the firm name + a friendly explanation of the OTP
 *     step that Microsoft may impose for non-MSA accounts.
 *   - Provides a "Continue to SharePoint" button that opens the portal
 *     root in a new tab. The recipient still needs to navigate through
 *     Microsoft's prompts on first visit; we don't try to intercept.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

export const Route = createFileRoute('/share/$token')({
  component: SharePage,
});

function SharePage(): React.JSX.Element {
  const { token } = Route.useParams();
  // The "token" carried on the URL is currently the clientSlug. A signed
  // token variant can be introduced without changing the URL shape.
  const info = useQuery(api.portalInvitations.lookupForSharePage, {
    clientSlug: token,
  });

  const portalUrl = `/c/${token}/desktop`;

  return (
    <div
      style={{
        maxWidth: 640,
        margin: '64px auto',
        padding: '24px 32px',
        fontFamily: 'var(--xp-font-body)',
        background: '#fff',
        border: '1px solid #ddd',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        borderRadius: 4,
      }}
    >
      <h1 style={{ marginTop: 0, fontSize: 24 }}>
        You&apos;re invited to a Repancy portal
      </h1>
      {info === undefined && (
        <p style={{ color: '#666' }}>Loading…</p>
      )}
      {info === null && (
        <>
          <p>
            We couldn&apos;t find the portal you&apos;re trying to open.
            The invitation may have been revoked.
          </p>
          <p style={{ marginTop: 24 }}>
            <a href="/">Return to repancy.site</a>
          </p>
        </>
      )}
      {info !== undefined && info !== null && (
        <>
          <p>
            <strong>{info.firmName}</strong> uses Repancy as their legal AI
            workbench, with documents stored in Microsoft SharePoint.
          </p>

          <section
            style={{
              marginTop: 20,
              padding: 16,
              background: '#f7f7fa',
              border: '1px solid #d8d8e0',
              borderRadius: 4,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>
              First time opening this link?
            </h2>
            <p style={{ marginTop: 8, marginBottom: 0, lineHeight: 1.5 }}>
              Microsoft may send you a one-time verification code by email
              the first time you open the SharePoint site. This is normal
              and means Microsoft is creating a guest account for you in{' '}
              {info.firmName}&apos;s organization. Enter the code on the
              page Microsoft shows you, and you&apos;ll be able to view the
              site immediately. Subsequent visits won&apos;t need
              verification.
            </p>
            <p
              style={{
                marginTop: 12,
                marginBottom: 0,
                lineHeight: 1.5,
                color: '#444',
              }}
            >
              You can use any email address — Microsoft Work or School,
              Outlook/Hotmail/Live, Gmail, or any provider. No Microsoft
              account is required up-front.
            </p>
          </section>

          <p style={{ marginTop: 24 }}>
            <a
              href={portalUrl}
              style={{
                display: 'inline-block',
                padding: '10px 18px',
                background: '#0078d4',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              Continue to {info.firmName} portal
            </a>
          </p>

          <p style={{ marginTop: 32, fontSize: 12, color: '#888' }}>
            Repancy and {info.firmName} never see the verification code —
            it&apos;s handled entirely by Microsoft. If you didn&apos;t
            expect this invitation, you can safely ignore it; no account
            is created until you redeem the link.
          </p>
        </>
      )}
    </div>
  );
}
